import { useState, useEffect } from 'react';
import { addMonths, subMonths } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, CalendarMonthIcon } from '@/components/ui/Icons';
import { Input } from '@/components/ui/Input/Input';
import { Select } from '@/components/ui/Select/Select';
import { Button } from '@/components/ui/Button/Button';
import { Modal } from '@/components/ui/Modal/Modal';
import { MONTH_NAMES } from '@/schedule/scheduleConstants';
import './ScheduleMonthSwitcher.css';

interface ScheduleMonthSwitcherProps {
  year: number;
  month: number;
  onMonthChange: (year: number, month: number) => void;
}

export function ScheduleMonthSwitcher({ year, month, onMonthChange }: ScheduleMonthSwitcherProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [tempMonth, setTempMonth] = useState(month);
  const [tempYear, setTempYear] = useState(year);

  useEffect(() => {
    setTempMonth(month);
    setTempYear(year);
  }, [year, month]);

  const label = `${MONTH_NAMES[month]} ${year}`;

  const handlePrev = () => {
    const d = new Date(year, month, 1);
    const prev = subMonths(d, 1);
    onMonthChange(prev.getFullYear(), prev.getMonth());
  };
  const handleNext = () => {
    const d = new Date(year, month, 1);
    const next = addMonths(d, 1);
    onMonthChange(next.getFullYear(), next.getMonth());
  };

  const handleApply = () => {
    onMonthChange(tempYear, tempMonth);
    setModalOpen(false);
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setTempMonth(now.getMonth());
    setTempYear(now.getFullYear());
  };

  return (
    <>
      <div className="schedule-month-switcher">
        <Button
          type="button"
          variant="outlined"
          size="s"
          className="schedule-month-switcher__nav-icon-btn"
          onClick={handlePrev}
          aria-label="Предыдущий месяц"
        >
          <ChevronLeftIcon size={18} aria-hidden />
        </Button>
        <Button
          type="button"
          variant="outlined"
          size="s"
          className="schedule-month-switcher__date-btn"
          onClick={() => setModalOpen(true)}
        >
          <CalendarMonthIcon size={18} aria-hidden />
          <span className="schedule-month-switcher__label">{label}</span>
        </Button>
        <Button
          type="button"
          variant="outlined"
          size="s"
          className="schedule-month-switcher__nav-icon-btn"
          onClick={handleNext}
          aria-label="Следующий месяц"
        >
          <ChevronRightIcon size={18} aria-hidden />
        </Button>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Выбор даты">
        <form onSubmit={(e) => { e.preventDefault(); handleApply(); }} className="form">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <Input
              label="Год"
              type="number"
              value={String(tempYear)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempYear(parseInt(e.target.value, 10) || new Date().getFullYear())}
              fullWidth
            />
            <Select
              label="Месяц"
              options={MONTH_NAMES.map((name, i) => ({ value: String(i), label: name }))}
              value={String(tempMonth)}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTempMonth(Number(e.target.value))}
              fullWidth
            />
            <div className="form__footer" style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', paddingTop: 'var(--space-2)' }}>
              <Button variant="outlined" type="button" onClick={goToCurrentMonth}>
                Текущий месяц
              </Button>
              <Button type="submit" variant="primary" onClick={handleApply}>
                Применить
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
}
